import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, MapPin, IndianRupee, Ruler, Calendar,
  FileText, MessageSquare, Users, Trash2, Plus, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../AuthContext";

const STATUSES = ["Prospecting", "Diligence", "Aggregation", "Legal", "Delivered", "On Hold"];

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [note, setNote] = useState("");
  const [docForm, setDocForm] = useState({ name: "", url: "" });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      if (isAdmin) {
        const { data: u } = await api.get("/users");
        setUsers(u);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/portal/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, { status });
      setProject(data);
      toast.success("Status updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/projects/${id}/notes`, { text: note.trim() });
      setProject(data);
      setNote("");
      toast.success("Note added.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addDoc = async (e) => {
    e.preventDefault();
    if (!docForm.name.trim() || !docForm.url.trim()) return;
    try {
      const { data } = await api.post(`/projects/${id}/documents`, docForm);
      setProject(data);
      setDocForm({ name: "", url: "" });
      toast.success("Document link added.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete project ${project.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted.");
      navigate("/portal/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleAssignee = async (empId) => {
    const assigned = project.assigned_employee_ids || [];
    const next = assigned.includes(empId)
      ? assigned.filter((x) => x !== empId)
      : [...assigned, empId];
    try {
      const { data } = await api.patch(`/projects/${id}`, {
        assigned_employee_ids: next,
      });
      setProject(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !project) {
    return (
      <div className="p-12 text-neutral-400 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading project…
      </div>
    );
  }

  const employeeMap = Object.fromEntries(users.map((u) => [u.id, u]));

  return (
    <div className="p-8 md:p-12 max-w-6xl" data-testid="project-detail-page">
      <Link
        to="/portal/dashboard"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-neutral-500 hover:text-[#D4AF37] mb-8"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Board
      </Link>

      <header className="mb-10 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="overline text-[#D4AF37] mb-2">{project.code} · {project.client_type}</div>
          <h1 className="font-display text-4xl md:text-5xl text-neutral-50 leading-tight">
            {project.name}
          </h1>
          <div className="mt-2 text-neutral-400">{project.client}</div>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="detail-status-select"
            value={project.status}
            onChange={(e) => changeStatus(e.target.value)}
            className="bg-[#08080A] border border-[#D4AF37]/40 text-[#D4AF37] py-2.5 px-4 text-xs tracking-[0.24em] uppercase focus:outline-none"
          >
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          {isAdmin && (
            <button
              data-testid="delete-project-btn"
              onClick={remove}
              className="border border-white/10 hover:border-red-400 text-neutral-400 hover:text-red-400 p-2.5"
              title="Delete project"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>

      {/* Facts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Fact icon={MapPin} label="Location" value={`${project.district}${project.taluk ? " · " + project.taluk : ""}`} />
        <Fact icon={Ruler} label="Area" value={project.area_acres ? `${project.area_acres} acres` : "—"} />
        <Fact icon={IndianRupee} label="Deal Value" value={project.deal_value_inr ? formatINR(project.deal_value_inr) : "—"} />
        <Fact icon={Calendar} label="Expected Close" value={project.expected_close_date || "—"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Left col: notes + docs */}
        <div className="md:col-span-2 space-y-10">
          {/* Notes */}
          <section data-testid="notes-section">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
              <div className="overline">Update Log</div>
            </div>
            <form onSubmit={addNote} className="mb-4">
              <textarea
                data-testid="note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Add an update — site visit notes, negotiation status, etc."
                className="w-full bg-[#0C0C10] border border-white/10 p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D4AF37]"
              />
              <div className="mt-2 flex justify-end">
                <button
                  data-testid="add-note-btn"
                  type="submit"
                  className="text-xs tracking-[0.24em] uppercase text-[#D4AF37] hover:text-[#F0D88B]"
                >
                  Add Note →
                </button>
              </div>
            </form>
            <div className="space-y-3">
              {(project.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="border-l-2 border-[#D4AF37]/40 pl-4 py-1">
                  <div className="text-neutral-200 text-sm leading-relaxed">{n.text}</div>
                  <div className="text-[0.65rem] tracking-widest uppercase text-neutral-500 mt-1">
                    {n.author_name} · {formatDate(n.created_at)}
                  </div>
                </div>
              ))}
              {(!project.notes || project.notes.length === 0) && (
                <div className="text-xs text-neutral-600 italic">No updates yet.</div>
              )}
            </div>
          </section>

          {/* Docs */}
          <section data-testid="documents-section">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-[#D4AF37]" />
              <div className="overline">Documents (link-based)</div>
            </div>
            <form onSubmit={addDoc} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4">
              <input
                data-testid="doc-name-input"
                value={docForm.name}
                onChange={(e) => setDocForm({ ...docForm, name: e.target.value })}
                placeholder="Document name"
                className="md:col-span-2 bg-[#0C0C10] border border-white/10 p-2.5 text-sm text-neutral-100 focus:outline-none focus:border-[#D4AF37]"
              />
              <input
                data-testid="doc-url-input"
                value={docForm.url}
                onChange={(e) => setDocForm({ ...docForm, url: e.target.value })}
                placeholder="Google Drive / Dropbox URL"
                className="md:col-span-2 bg-[#0C0C10] border border-white/10 p-2.5 text-sm text-neutral-100 focus:outline-none focus:border-[#D4AF37]"
              />
              <button
                data-testid="add-doc-btn"
                type="submit"
                className="border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#08080A] text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </form>
            <div className="space-y-2">
              {(project.documents || []).slice().reverse().map((d) => (
                <a
                  key={d.id}
                  href={d.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-center justify-between gap-3 border border-white/8 hover:border-[#D4AF37]/60 p-3 group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-[#D4AF37] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-neutral-100 text-sm truncate group-hover:text-[#D4AF37]">
                        {d.name}
                      </div>
                      <div className="text-[0.65rem] text-neutral-500 tracking-wider uppercase truncate">
                        Uploaded by {d.uploader_name} · {formatDate(d.uploaded_at)}
                      </div>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-neutral-500 shrink-0" />
                </a>
              ))}
              {(!project.documents || project.documents.length === 0) && (
                <div className="text-xs text-neutral-600 italic">No documents linked yet. Paste a Google Drive / Dropbox share URL above.</div>
              )}
            </div>
          </section>
        </div>

        {/* Right: assignees */}
        <div className="space-y-6">
          <section data-testid="assignees-section">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <div className="overline">Assigned</div>
            </div>
            <div className="space-y-2">
              {(project.assigned_employee_ids || []).map((eid) => {
                const emp = employeeMap[eid];
                return (
                  <div key={eid} className="border border-white/8 p-3">
                    <div className="text-neutral-100 text-sm">
                      {emp?.name || eid}
                    </div>
                    <div className="text-[0.65rem] text-neutral-500 tracking-wider uppercase">
                      {emp?.designation || emp?.role || "employee"}
                    </div>
                  </div>
                );
              })}
              {(project.assigned_employee_ids || []).length === 0 && (
                <div className="text-xs text-neutral-600 italic">Nobody assigned.</div>
              )}
            </div>

            {isAdmin && users.length > 0 && (
              <div className="mt-5">
                <div className="overline mb-3 text-neutral-500">Manage</div>
                <div className="flex flex-wrap gap-2">
                  {users
                    .filter((u) => u.role !== "admin")
                    .map((emp) => {
                      const active = (project.assigned_employee_ids || []).includes(emp.id);
                      return (
                        <button
                          key={emp.id}
                          data-testid={`toggle-assignee-${emp.id}`}
                          onClick={() => toggleAssignee(emp.id)}
                          className={`px-3 py-1.5 border text-[0.65rem] tracking-widest uppercase transition-colors ${
                            active
                              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                              : "border-white/15 text-neutral-400 hover:border-[#D4AF37]/50"
                          }`}
                        >
                          {emp.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </section>

          {/* Meta */}
          <section className="border border-white/8 p-4 text-xs space-y-2">
            <MetaRow label="Start" value={project.start_date || "—"} />
            <MetaRow label="Expected Close" value={project.expected_close_date || "—"} />
            <MetaRow label="Updated" value={formatDate(project.updated_at)} />
            <MetaRow label="Created" value={formatDate(project.created_at)} />
          </section>
        </div>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/8 bg-[#0C0C10] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
        <div className="overline text-neutral-500">{label}</div>
      </div>
      <div className="text-neutral-100 font-display text-lg leading-tight">{value}</div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex justify-between text-neutral-400">
      <span className="tracking-widest uppercase text-[0.6rem] text-neutral-500">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function formatINR(n) {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
